import React, { useState } from "react";
import classNames from "classnames";
import { ClockIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@design-system/Popover/Popover";
import { useSeededOpen } from "@design-system/hooks/useSeededOpen";
import {
    defaultDurationPresets,
    formatDuration,
    parseDuration,
} from "./TimePicker.lib";
import styles from "./TimePicker.module.scss";
import type { DisableableTrigger } from "@design-system/overlay/triggerButtonProps";

interface TimePickerProps {
    /** Duration in minutes. */
    value?: number;
    onChange: (value: number | undefined) => void;
    /** Custom trigger element. Defaults to a secondary button. */
    trigger?: DisableableTrigger;
    label?: string;
    placeholder?: string;
    triggerLabel?: string;
    /** Preset durations in minutes, shown as a three-column grid. */
    presets?: readonly number[];
    disabled?: boolean;
    isClearable?: boolean;
    /** The clearable footer button's text. */
    removeLabel?: string;
    /** Controls the popup when given; `onOpenChange` still reports every request. */
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    className?: string;
    hideIcon?: boolean;
}

export const TimePicker = ({
    value,
    onChange,
    trigger,
    label = "Set estimate",
    placeholder = "45m",
    triggerLabel = "Set estimate",
    presets = defaultDurationPresets,
    disabled = false,
    isClearable = true,
    removeLabel = "Remove current estimate",
    open,
    onOpenChange,
    className,
    hideIcon = false,
}: TimePickerProps) => {
    const [inputValue, setInputValue] = useState("");
    const [hasError, setHasError] = useState(false);
    const formattedValue = formatDuration(value);
    // The input starts empty, so an open mount seeds it too.
    const { isOpen, setOpen } = useSeededOpen({
        open,
        onOpenChange,
        initialSeededOpen: false,
        onSeed: () => {
            setInputValue(formattedValue);
            setHasError(false);
        },
    });

    const commitInput = () => {
        const trimmedInput = inputValue.trim();
        if (trimmedInput.length === 0) {
            if (isClearable) {
                onChange(undefined);
                setOpen(false);
            }
            return;
        }
        const parsedMinutes = parseDuration(trimmedInput);
        if (parsedMinutes === undefined) {
            setHasError(true);
            return;
        }
        onChange(parsedMinutes);
        setOpen(false);
    };

    const selectPreset = (presetMinutes: number) => {
        onChange(presetMinutes);
        setOpen(false);
    };

    return (
        <Popover open={isOpen} onOpenChange={setOpen}>
            <PopoverTrigger disabled={disabled}>
                {trigger ? (
                    React.cloneElement(trigger, { disabled })
                ) : (
                    <Button
                        type="button"
                        variant="primary"
                        size="md"
                        disabled={disabled}
                        aria-label={label}
                        leftIcon={hideIcon ? undefined : <ClockIcon />}
                    >
                        {formattedValue.length > 0
                            ? formattedValue
                            : triggerLabel}
                    </Button>
                )}
            </PopoverTrigger>
            <PopoverContent
                className={classNames(styles.popover, className)}
                align="start"
                aria-label={label}
            >
                <div className={styles.inputSection}>
                    <div className={styles.label}>{label}</div>
                    <div
                        className={classNames(styles.inputWrapper, {
                            [styles.inputWrapperError]: hasError,
                        })}
                    >
                        <ClockIcon className={styles.inputIcon} />
                        <input
                            className={styles.input}
                            value={inputValue}
                            placeholder={placeholder}
                            autoFocus
                            aria-invalid={hasError}
                            onChange={(event) => {
                                setInputValue(event.target.value);
                                setHasError(false);
                            }}
                            onKeyDown={(event) => {
                                if (event.nativeEvent.isComposing) {
                                    event.stopPropagation();
                                    return;
                                }
                                if (event.key === "Enter") {
                                    commitInput();
                                }
                            }}
                            aria-label={label}
                        />
                    </div>
                </div>
                <div className={styles.presetsGrid}>
                    {presets.map((presetMinutes) => (
                        <button
                            key={presetMinutes}
                            type="button"
                            className={classNames(styles.preset, {
                                [styles.activePreset]: presetMinutes === value,
                            })}
                            onClick={() => selectPreset(presetMinutes)}
                        >
                            {formatDuration(presetMinutes)}
                        </button>
                    ))}
                </div>
                {isClearable && value !== undefined && (
                    <div className={styles.footer}>
                        <Button
                            type="button"
                            variant="primary"
                            size="md"
                            block
                            onClick={() => {
                                onChange(undefined);
                                setOpen(false);
                            }}
                        >
                            {removeLabel}
                        </Button>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
};
